// components/common/GoogleAddressInput.tsx
import { useState, useCallback, useEffect } from "react";
import { Input } from "antd";
import { Autocomplete, useJsApiLoader } from "@react-google-maps/api";
import {
  getGoogleMapsApiKey,
  GOOGLE_MAP_SCRIPT_ID,
  GOOGLE_MAPS_LOADER_LIBRARIES,
} from "@/lib/googleMapsLoaderConstants";

/// <reference types="google.maps" />

interface AddressDetails {
  formatted_address: string;
  latitude: string;
  longitude: string;
  place_id: string;
  name: string;
  display_name: string;
  country: string;
  state: string;
  district: string;
  city: string;
  street: string;
  postal_code: string;
  phone: string;
}

interface GoogleAddressInputProps {
  value?: string | AddressDetails;
  onChange?: (details?: AddressDetails) => void;
  placeholder?: string;
  onAddressSelect?: (address: string, lat?: number, lng?: number) => void;
  style?: React.CSSProperties;
  disabled?: boolean;
  className?: string;
  countryRestriction?: string;
  types?: string[];
}

const getDisplayValue = (val: string | AddressDetails | undefined): string => {
  if (typeof val === "string") {
    return val;
  }
  if (val && typeof val === "object") {
    const cleanedName = val?.name?.trim().replace(/\.$/, "") ?? "";
    const display = val?.display_name?.trim() ?? "";
    return `${cleanedName}, ${display}`.trim();
  }
  return "";
};

function useSyncedAddressInput(
  value: GoogleAddressInputProps["value"],
  onChange: GoogleAddressInputProps["onChange"]
) {
  const [inputValue, setInputValue] = useState(
    () => getDisplayValue(value) ?? ""
  );

  useEffect(() => {
    if (value === "" || value === undefined) {
      setInputValue("");
      return;
    }
    setInputValue(getDisplayValue(value) ?? "");
  }, [value]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setInputValue(newValue);
    if (newValue === "") {
      onChange?.(undefined);
    }
  };

  const clear = () => {
    setInputValue("");
    onChange?.(undefined);
  };

  return { inputValue, setInputValue, handleInputChange, clear };
}

function PlainGoogleAddressInput({
  value,
  onChange,
  placeholder = "Enter address",
  disabled = false,
  className,
  style,
}: GoogleAddressInputProps) {
  const { inputValue, handleInputChange, clear } = useSyncedAddressInput(
    value,
    onChange
  );

  return (
    <Input
      value={inputValue}
      onChange={handleInputChange}
      placeholder={placeholder}
      disabled={disabled}
      allowClear
      onClear={clear}
      className={className}
      style={style}
    />
  );
}

type WithKeyProps = GoogleAddressInputProps & { apiKey: string };

function GoogleAddressInputWithLoader({
  apiKey,
  value,
  onChange,
  placeholder = "Enter address",
  disabled = false,
  className,
  countryRestriction,
  types = ["address"],
  style,
}: WithKeyProps) {
  const { inputValue, setInputValue, handleInputChange, clear } =
    useSyncedAddressInput(value, onChange);
  const [autocomplete, setAutocomplete] =
    useState<google.maps.places.Autocomplete | null>(null);

  const { isLoaded, loadError } = useJsApiLoader({
    id: GOOGLE_MAP_SCRIPT_ID,
    googleMapsApiKey: apiKey,
    libraries: [...GOOGLE_MAPS_LOADER_LIBRARIES],
  });

  const onAutocompleteLoad = useCallback(
    (autocompleteInstance: google.maps.places.Autocomplete) => {
      setAutocomplete(autocompleteInstance);
    },
    []
  );

  const onPlaceChanged = useCallback(() => {
    if (autocomplete !== null) {
      const place = autocomplete.getPlace();

      if (place.formatted_address) {
        const addressComponents = place.address_components;
        let city = "";
        let state = "";
        let country = "";
        let district = "";
        let postalCode = "";
        let street = "";
        let streetNumber = "";

        addressComponents?.forEach(
          (component: { types: string[]; long_name: string }) => {
            const componentTypes = component.types;

            if (componentTypes.includes("locality")) {
              city = component.long_name;
            } else if (
              componentTypes.includes("sublocality_level_1") ||
              componentTypes.includes("sublocality")
            ) {
              if (!city) {
                city = component.long_name;
              }
            } else if (componentTypes.includes("administrative_area_level_1")) {
              state = component.long_name.toUpperCase();
            } else if (componentTypes.includes("administrative_area_level_2")) {
              district = component.long_name;
            } else if (componentTypes.includes("country")) {
              country = component.long_name;
            } else if (componentTypes.includes("postal_code")) {
              postalCode = component.long_name;
            } else if (componentTypes.includes("route")) {
              street = component.long_name;
            } else if (componentTypes.includes("street_number")) {
              streetNumber = component.long_name;
            }
          }
        );

        const fullStreet = streetNumber ? `${streetNumber} ${street}` : street;

        const addressDetails: AddressDetails = {
          formatted_address: place.formatted_address,
          latitude: place.geometry?.location?.lat()?.toString() ?? "",
          longitude: place.geometry?.location?.lng()?.toString() ?? "",
          place_id: place.place_id ?? "",
          name: place.name ?? "",
          display_name: place.formatted_address ?? "",
          country,
          state,
          district,
          city,
          street: fullStreet,
          postal_code: postalCode,
          phone:
            place.formatted_phone_number ??
            place.international_phone_number ??
            "",
        };

        onChange?.(addressDetails);
        const displayValue =
          `${place.name ?? ""}, ${place.formatted_address ?? ""}`.trim();
        setInputValue(displayValue);
      }
    }
  }, [autocomplete, onChange, setInputValue]);

  if (loadError) {
    return (
      <Input
        value={inputValue}
        onChange={handleInputChange}
        placeholder={placeholder}
        disabled={disabled}
        allowClear
        onClear={clear}
        className={className}
        style={style}
      />
    );
  }

  if (!isLoaded) {
    return (
      <Input
        value={inputValue}
        onChange={handleInputChange}
        placeholder="Loading address service..."
        disabled={disabled}
        allowClear
        onClear={clear}
        className={className}
        style={style}
      />
    );
  }

  const autocompleteOptions: google.maps.places.AutocompleteOptions = {
    types,
  };

  if (countryRestriction) {
    autocompleteOptions.componentRestrictions = { country: countryRestriction };
  }

  return (
    <Autocomplete
      onLoad={onAutocompleteLoad}
      onPlaceChanged={onPlaceChanged}
      options={autocompleteOptions}
    >
      <Input
        value={inputValue}
        onChange={handleInputChange}
        placeholder={placeholder}
        disabled={disabled}
        allowClear
        onClear={clear}
        className={className}
        style={style}
      />
    </Autocomplete>
  );
}

const GoogleAddressInput: React.FC<GoogleAddressInputProps> = props => {
  const apiKey = getGoogleMapsApiKey();
  if (!apiKey) {
    return <PlainGoogleAddressInput {...props} />;
  }
  return <GoogleAddressInputWithLoader {...props} apiKey={apiKey} />;
};

export default GoogleAddressInput;
